import { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, MoveLeft, MoveRight, RotateCcw, Save, Palette } from "lucide-react";
import { authedFetch } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";

export const PersistentLayout = ({
  scope,
  items,
  className = "",
  ariaLabel = "Layout",
  editable = false,
  direction = "horizontal",
  showHint = false,
}) => {
  const itemKey = items.map((item) => item.id).join("|");
  const defaultOrder = useMemo(() => itemKey.split("|").filter(Boolean), [itemKey]);
  const [order, setOrder] = useState(defaultOrder);
  const [menu, setMenu] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [styleSettings, setStyleSettings] = useState({ fontScale: "normal", spacing: "normal", alignment: "start" });

  useEffect(() => {
    let active = true;
    authedFetch("/layouts/" + scope)
      .then(async (res) => res.ok ? res.json() : null)
      .then((data) => {
        if (!active) return;
        const saved = data?.item_order;
        const valid = Array.isArray(saved) && defaultOrder.every((id) => saved.includes(id));
        setOrder(valid ? [...saved.filter((id) => defaultOrder.includes(id)), ...defaultOrder.filter((id) => !saved.includes(id))] : defaultOrder);
        setStyleSettings({ fontScale: "normal", spacing: "normal", alignment: "start", ...(data?.style_settings || {}) });
        setDirty(false);
      })
      .catch(() => {
        if (active) setOrder(defaultOrder);
      });
    return () => { active = false; };
  }, [scope, itemKey, defaultOrder]);

  useEffect(() => {
    const close = () => setMenu(null);
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
    };
  }, []);

  const move = (id, amount) => {
    setOrder((current) => {
      const from = current.indexOf(id);
      const to = from + amount;
      if (from < 0 || to < 0 || to >= current.length) return current;
      const next = [...current];
      [next[from], next[to]] = [next[to], next[from]];
      return next;
    });
    setDirty(true);
    setMenu(null);
  };

  const save = async () => {
    setSaving(true);
    const res = await authedFetch("/admin/layouts/" + scope, {
      method: "PUT",
      body: JSON.stringify({ item_order: order, style_settings: styleSettings }),
    });
    setSaving(false);
    if (!res.ok) {
      toast.error("Could not save this layout.");
      return;
    }
    setDirty(false);
    toast.success("Layout saved and live.");
  };

  const reset = () => {
    setOrder(defaultOrder);
    setStyleSettings({ fontScale: "normal", spacing: "normal", alignment: "start" });
    setDirty(true);
    setMenu(null);
  };

  const byId = Object.fromEntries(items.map((item) => [item.id, item]));
  const arranged = order.map((id) => byId[id]).filter((item) => item && item.node !== null && item.node !== false);
  const menuIndex = menu ? order.indexOf(menu.id) : -1;
  const vertical = direction === "vertical";
  const layoutStyle = {
    gap: styleSettings.spacing === "compact" ? "0.35rem" : styleSettings.spacing === "relaxed" ? "1.25rem" : undefined,
    zoom: styleSettings.fontScale === "small" ? 0.9 : styleSettings.fontScale === "large" ? 1.1 : 1,
    ...(vertical ? { alignItems: styleSettings.alignment === "center" ? "center" : styleSettings.alignment === "end" ? "flex-end" : "stretch" } : { justifyContent: styleSettings.alignment === "center" ? "center" : styleSettings.alignment === "end" ? "flex-end" : "flex-start" }),
  };
  const setStyle = (key, value) => { setStyleSettings((current) => ({ ...current, [key]: value })); setDirty(true); };

  return (
    <div className="relative">
      {editable && (
        <div className="mb-3 flex flex-wrap items-end gap-3 rounded-md border border-slate-200 bg-white p-3" data-testid={"layout-style-controls-" + scope}>
          <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><Palette className="h-4 w-4" /> Visual style</span>
          <label className="text-xs text-slate-600">Font size<select value={styleSettings.fontScale} onChange={(e) => setStyle("fontScale", e.target.value)} className="ml-2 h-8 rounded border border-slate-200 bg-white px-2 text-xs"><option value="small">Small</option><option value="normal">Standard</option><option value="large">Large</option></select></label>
          <label className="text-xs text-slate-600">Spacing<select value={styleSettings.spacing} onChange={(e) => setStyle("spacing", e.target.value)} className="ml-2 h-8 rounded border border-slate-200 bg-white px-2 text-xs"><option value="compact">Compact</option><option value="normal">Standard</option><option value="relaxed">Relaxed</option></select></label>
          <label className="text-xs text-slate-600">Alignment<select value={styleSettings.alignment} onChange={(e) => setStyle("alignment", e.target.value)} className="ml-2 h-8 rounded border border-slate-200 bg-white px-2 text-xs"><option value="start">Left / top</option><option value="center">Centre</option><option value="end">Right / bottom</option></select></label>
        </div>
      )}
      <div className={className} aria-label={ariaLabel} style={layoutStyle}>
        {arranged.map((item) => (
          <div
            key={item.id}
            className={vertical ? "w-full" : "shrink-0"}
            onContextMenu={editable ? (event) => {
              event.preventDefault();
              setMenu({ id: item.id, x: event.clientX, y: event.clientY });
            } : undefined}
            title={editable ? ((item.label || "") + " — right-click to move") : item.title}
            data-layout-item={item.id}
          >
            {item.node}
          </div>
        ))}
        {editable && dirty && (
          <div className={vertical ? "sticky bottom-3 z-20 flex justify-end" : "shrink-0"}>
            <Button type="button" size="sm" onClick={save} disabled={saving} data-testid={"save-layout-" + scope}>
              <Save className="mr-1.5 h-4 w-4" /> {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        )}
        {editable && showHint && !dirty && (
          <span className="shrink-0 self-center px-1 text-[11px] text-slate-400">Right-click an item to rearrange</span>
        )}
      </div>

      {editable && menu && (
        <div
          className="fixed z-[100] w-48 rounded-md border border-slate-200 bg-white p-1 shadow-xl"
          style={{ left: Math.min(menu.x, window.innerWidth - 205), top: Math.min(menu.y, window.innerHeight - 150) }}
          role="menu"
          data-testid="layout-context-menu"
          onClick={(event) => event.stopPropagation()}
        >
          <button type="button" onClick={() => move(menu.id, -1)} disabled={menuIndex <= 0} className="flex w-full items-center rounded px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 disabled:opacity-40">
            {vertical ? <ArrowUp className="mr-2 h-4 w-4" /> : <MoveLeft className="mr-2 h-4 w-4" />}
            {vertical ? "Move up" : "Move left"}
          </button>
          <button type="button" onClick={() => move(menu.id, 1)} disabled={menuIndex < 0 || menuIndex >= order.length - 1} className="flex w-full items-center rounded px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 disabled:opacity-40">
            {vertical ? <ArrowDown className="mr-2 h-4 w-4" /> : <MoveRight className="mr-2 h-4 w-4" />}
            {vertical ? "Move down" : "Move right"}
          </button>
          <button type="button" onClick={reset} className="flex w-full items-center rounded px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100">
            <RotateCcw className="mr-2 h-4 w-4" /> Reset default
          </button>
        </div>
      )}
    </div>
  );
};

export const EditableLayoutRow = (props) => <PersistentLayout {...props} direction="horizontal" />;
